<template>
    <div class="companies">
        <MainHeader
            class="companies__header"
            title="Уровни и доступы"
            :number="responseCompanies.companies?.length ? numberCompanies : ''"
            btnText="Новый уровень"
            @btnClick="$router.push(`/companies/new${queryParams}`)"
        />
        <div class="companies__row">
            <div class="companies__segmented-control">
              <VSegmentedControlQuery
                  :items="filteringOptions"
              />
            </div>
            <VDefaultInput
                v-model="search"
                placeholder="Введите название уровня"
                border
                class="companies__search"
            >
                <template #iconLeft>
                    <img src="@/assets/images/icons/search.svg" alt="search"/>
                </template>
                <template #iconRight>
                    <div
                        class="companies__search-clear"
                        v-if="search"
                        @click="clearSearch"
                    >
                        <img src="@/assets/images/icons/x.svg" alt="search"/>
                    </div>
                </template>
            </VDefaultInput>
        </div>

        <div class="companies__no-content" v-if="errorGet">
            <VNoContent
                title="Ошибка"
                description="Произошла ошибка. Пожалуйста, попробуйте позже"
            />
        </div>

        <div class="companies__content" v-if="!errorGet ">
            <div
                class="companies__companies"
            >
                <CompanyCard
                    v-if="!skeleton && responseCompanies.companies && responseCompanies.companies.length"
                    class="companies__company"
                    v-for="company in responseCompanies.companies"
                    :key="company.id"
                    :company="company"
                />

              <VSkeleton v-if="skeleton" v-for="item in  [...Array(9).keys()]" height="111" variants="CARD" />
            </div>
            <div class="companies__no-content" v-if="responseCompanies.companies && !responseCompanies.companies.length">
                <VNoContent
                    v-if="!search"
                    title="Список подразделений пуст"
                    description="По выбранному фильтру ничего не найдено"
                />
                <VNoContent
                    v-if="search"
                    title="Ничего не найдено"
                    description="Проверьте пожалуйста, правильно ли вы написали название"
                />
            </div>

            <div
                class="companies__pagination"
                v-if="responseCompanies.pages_count > 1"
            >
                <VButtonNew
                    class="companies__pagination-btn"
                    label="Загрузить еще"
                    style-type="secondary"
                    @click="loadMore()"
                    v-if="page < responseCompanies.pages_count && showPagination"
                />
                <VPaginateQuery
                    :pageCount="responseCompanies.pages_count"
                    class="companies__pagination-pag"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted, watch, reactive, computed} from "vue";
import companies from "@/api/companies";
import MainHeader from "@/components/MainHeader";
import CompanyCard from "@/components/CompanyCard";
import VSkeleton from "@/components/UI/VSkeleton/index.vue";
import VSegmentedControlQuery from "@/components/UI/VSegmentedControlQuery/index.vue";
import VPaginateQuery from "@/components/UI/VPaginateQuery/index.vue";
import {useRoute, useRouter} from "vue-router";

const route = useRoute();
const router = useRouter()
const filteringOptions = [
    {
        id: "all",
        label: "Все",
    },
    {
        id: "1",
        label: "Активные",
    },
    {
        id: "0",
        label: "Заблокированные",
    },
];
const activeFilter = ref("all");
const search = ref("");
const page = ref(1);
const showPagination = ref(true)
const errorGet = ref(false)
const skeleton = ref(true)
const responseCompanies = ref({});
const numberCompanies = ref(0);
const getCompanies = async () => {
  let params = ''
  if (route.query?.tabs === "all") {
    params = {}
  } else {
    params = { has_contract: route.query?.tabs };
  }
  params.search = route.query?.search;
  try {
    skeleton.value = true
    responseCompanies.value = await companies.getCompanies(params);
    numberCompanies.value = responseCompanies.value.companies_count;
    skeleton.value = false
  } catch (error) {
    errorGet.value = true
    console.log(error);
  }
};

const loadMore = async (num) => {
    showPagination.value = false
    page.value = num ? num : page.value + 1;

    const params = {
        search: search.value ? search.value : "",
        page: page.value,
    };

    if (activeFilter.value !== "all") {
        params.has_contract = activeFilter.value;
    }

    try {
        const response = await companies.getCompanies(params);
        if (num) responseCompanies.value.companies = [];
        responseCompanies.value.companies.push(...response.companies);
        showPagination.value = true
    } catch (error) {
        errorGet.value = true
        console.log(error);
    }
};

const updateParamsInQuery = () => {
  search.value = route.query?.search;
  page.value = route.query?.page || 1;
}

onMounted(() => {
  updateParamsInQuery()
  getCompanies()
})

const clearSearch = () => {
  search.value = "";
  getCompanies()
};

watch(search, (newValue) => {
  router.replace({query: {...route.query, search: newValue}});
})

watch(() => route.query, (newValue) => {
  getCompanies()
}, {deep: true})


const queryParams = computed(() => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(route.query)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  }
  return '?' + searchParams.toString()
})
</script>

<style scoped lang="scss">
.companies {
    padding-bottom: 57px;

    &__header {
        margin-top: 24px;
    }

    &__row {
        margin-top: 24px;
        display: flex;
        justify-content: space-between;
    }

    &__search {
        width: 437px;

        &::v-deep(.v-input__input) {
            &::placeholder {
                color: $dark-blue;
                opacity: 1;
            }
        }
    }

    &__search-clear {
        cursor: pointer;
    }

    &__content {
        min-height: calc(100vh - 543px);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    &__companies {
        margin-top: 8px;
        margin-left: -32px;
        display: flex;
        flex-wrap: wrap;
    }

    &__company {
        width: calc(33.33% - 32px);
        margin-top: 24px;
        margin-left: 32px;
    }

    &__no-content {
        width: 100%;
        height: calc(100vh - 543px);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__pagination {
        position: relative;
        width: 100%;
        height: 48px;
        margin-top: 32px;
        display: flex;
        justify-content: center;
    }

    &__pagination-btn {
        width: 156px;
        padding: 0 10px;
        font-weight: 600;
        border-radius: 8px;

        &:active {
            background-color: #E5EEF8 !important;
            color: #1447AF !important;
        }
    }

    &__segmented-control {
      width: 640px;
    }

    &__pagination-pag {
        position: absolute;
        top: 0;
        right: 0;
    }
}
</style>
